import { performRequest } from '@celito.clients/network';

import Endpoints from '../config/endpoint';
import { ApiMethodType } from '../enums/api-constants';
import { DocumentDownloadActionEnum } from '../enums/download-document-action.enum';
import {
  DocCertificateData,
  DocData,
  EditDocumentData,
} from '../types/document-type';

const CONTROLLED_DOCS_OBJECT_NAME = 'controlled_document__a';

/**
 * Fetches document data from the backend API for downloading or viewing.
 *
 * @param docId - The unique identifier of the document to download
 * @param objectName - The object type (e.g., 'controlled_document__a')
 * @param recordName - The name of the record that owns this document
 * @param version - The version number of the document
 * @param downloaAction - The download action type (Download, DownloadRaw, View)
 * @param recordVersion - The version of the parent record
 * @param attributeName - The attribute name that identifies the document field.
 *                        This parameter is CRITICAL for attachment downloads.
 *
 *                        IMPORTANT: When downloading attachments (not the main document),
 *                        this parameter MUST be passed (e.g., 'attachments__a') so the
 *                        backend can:
 *                        1. Identify that this is an attachment (not the main 'file__a' document)
 *                        2. Look up the attribute's pdfConversionOptions configuration
 *                        3. Apply header/footer watermarks if 'includeHeaderAndFooter' is enabled
 *
 *                        If this parameter is not passed for attachments, the backend cannot
 *                        distinguish between main documents and attachments, and header/footer
 *                        watermarks will NOT be applied even if configured.
 *
 * @returns Promise<DocData> - The document data including file buffer, metadata, etc.
 */
export const getDocumentData = async (
  docId: string,
  objectName: string,
  recordName?: string,
  version?: string,
  downloaAction = DocumentDownloadActionEnum.Download,
  recordVersion?: string,
  attributeName?: string
) => {
  const response = await performRequest<DocData>(
    `${Endpoints.DOCUMENT_DATA_URL}/${docId}`,
    ApiMethodType.GET,
    null,
    {
      objectName,
      action: downloaAction,
      recordName: recordName,
      versionNumber: version,
      recordVersion: recordVersion,
      // Only include attributeName in the request if it has a value.
      // This tells the backend which attribute field the document belongs to,
      // enabling proper header/footer handling for attachments.
      ...(attributeName && { attributeName }),
    }
  );
  return response.data;
};

export const deleteDocuments = async (
  documentIds: string[],
  objectName: string,
  attributeName: string,
  recordName: string
) => {
  const response = await performRequest<DocData>(
    Endpoints.DOCUMENT_DATA_URL,
    ApiMethodType.DELETE,
    {
      documentIds,
      objectName,
      attributeName,
      recordName,
    }
  );
  return response.data;
};

export const deleteDocument = async (
  documentId: string,
  objectName: string,
  attributeName: string,
  recordName: string,
  version?: string
) => {
  const response = await performRequest<DocData>(
    `${Endpoints.DOCUMENT_DATA_URL}/${documentId}`,
    ApiMethodType.DELETE,
    {
      objectName,
      attributeName,
      recordName,
      documentVersion: version,
    }
  );
  return response.data;
};

export const getEditDocumentUrl = async (
  id: string,
  accessToken: string,
  version: string,
  taskName: string | undefined,
  viewType: string
) => {
  const response = await performRequest<EditDocumentData>(
    `${Endpoints.WOPI_URL}/${Endpoints.GENERATE_URL}/${id}`,
    ApiMethodType.GET,
    null,
    {
      access_token: accessToken,
      version,
      ...(taskName ? { taskName } : {}),
      viewType,
    }
  );
  return response.data;
};

export const getDocumentCertificateMetadata = async (recordName: string) => {
  const response = await performRequest<DocCertificateData>(
    `${Endpoints.DOCUMENT_CERTIFICATE_METADATA_DATA_URL}/${recordName}`,
    ApiMethodType.GET,
    null,
    {
      objectName: CONTROLLED_DOCS_OBJECT_NAME,
    }
  );

  return response.data;
};
